import React from "react";
import { JobKinds, JobKind } from "../supabase/functions/_shared/jobKinds";
import PrinterIcon from "./icons/PrinterIcon";
import CheckCircleIcon from "./icons/CheckCircleIcon";
import AlertTriangleIcon from "./icons/AlertTriangleIcon";
import GavelIcon from "./icons/GavelIcon";
import { Sparkles, RefreshCw } from "lucide-react";
import i18n from "../lib/i18n";

// Ensure the management namespace (where job_kinds.* live) is loaded before
// the getters / toast builders below resolve their keys.
void i18n.loadNamespaces("management");

/** Shorthand: translate a management:job_kinds.* key. */
const jt = (key: string, opts?: Record<string, unknown>): string =>
  i18n.t(`management:job_kinds.${key}`, opts ?? {});

export interface JobKindMeta {
  kind: JobKind;
  shortLabel: string;
  fullLabel: string;
  verbPresent: string; // present-tense verb, e.g. generation
  verbProgressive: string; // progressive verb, e.g. generation in progress
  verbPast: string; // past-tense verb, e.g. generated
  // Added "maintenance" for internal background tasks (e.g. distance refresh)
  category:
    | "mission-orders"
    | "emails"
    | "messaging"
    | "exports"
    | "maintenance"
    | "disciplinary"
    | "statements"
    | "audit"
    | "accounting";
  isBulk: boolean;
  icon: React.ReactNode;
  hasArtifact?: boolean;
  phaseLabels?: Record<string, string>;
  /** Ordered list of phase weights (must sum to 100 ideally). Mirrors backend PHASE_WEIGHTS for accurate progress */
  phaseWeights?: { name: string; weight: number }[];
  successToast?: (job: any) => string;
  failureToast?: (job: any) => string;
}

export const JOB_KIND_META: Record<JobKind, JobKindMeta> = {
  [JobKinds.MissionOrdersBulkPdf]: {
    kind: JobKinds.MissionOrdersBulkPdf,
    get shortLabel() { return jt("mission_orders_bulk_pdf.short"); },
    get fullLabel() { return jt("mission_orders_bulk_pdf.full"); },
    get verbPresent() { return jt("mission_orders_bulk_pdf.verb_present"); },
    get verbProgressive() { return jt("mission_orders_bulk_pdf.verb_progressive"); },
    get verbPast() { return jt("mission_orders_bulk_pdf.verb_past"); },
    category: "mission-orders",
    isBulk: true,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    // No phases for chunked jobs - progress shown via chunk display instead
    successToast: (job) => {
      const mode = job.result?.mode || job.meta?.mode;
      const succeeded =
        job.result?.totalOrders || job.result?.successfulOrders || 0;

      if (mode === "chunked_merged") {
        return jt("mission_orders_bulk_pdf.success_chunked", {
          count: succeeded,
          chunks: job.result?.totalChunks || 0,
        });
      }

      return succeeded > 0
        ? jt("mission_orders_bulk_pdf.success_other", { count: succeeded })
        : jt("mission_orders_bulk_pdf.success_empty");
    },
    failureToast: (job) => {
      const mode = job.result?.mode || job.meta?.mode;
      if (mode === "chunked" || mode === "chunked_merged") {
        return jt("mission_orders_bulk_pdf.failure_chunked", {
          error: job.error || jt("error_fallback"),
        });
      }
      return jt("mission_orders_bulk_pdf.failure", {
        error: job.error || jt("error_fallback_unknown"),
      });
    },
  },
  [JobKinds.MissionOrdersBulkPdfChunk]: {
    kind: JobKinds.MissionOrdersBulkPdfChunk,
    get shortLabel() { return jt("mission_orders_bulk_pdf_chunk.short"); },
    get fullLabel() { return jt("mission_orders_bulk_pdf_chunk.full"); },
    get verbPresent() { return jt("mission_orders_bulk_pdf_chunk.verb_present"); },
    get verbProgressive() { return jt("mission_orders_bulk_pdf_chunk.verb_progressive"); },
    get verbPast() { return jt("mission_orders_bulk_pdf_chunk.verb_past"); },
    category: "mission-orders",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: false,
  },
  [JobKinds.MissionOrdersBulkPdfMerge]: {
    kind: JobKinds.MissionOrdersBulkPdfMerge,
    get shortLabel() { return jt("mission_orders_bulk_pdf_merge.short"); },
    get fullLabel() { return jt("mission_orders_bulk_pdf_merge.full"); },
    get verbPresent() { return jt("mission_orders_bulk_pdf_merge.verb_present"); },
    get verbProgressive() { return jt("mission_orders_bulk_pdf_merge.verb_progressive"); },
    get verbPast() { return jt("mission_orders_bulk_pdf_merge.verb_past"); },
    category: "mission-orders",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
  },
  [JobKinds.MissionOrdersSinglePdf]: {
    kind: JobKinds.MissionOrdersSinglePdf,
    get shortLabel() { return jt("mission_orders_single_pdf.short"); },
    get fullLabel() { return jt("mission_orders_single_pdf.full"); },
    get verbPresent() { return jt("mission_orders_single_pdf.verb_present"); },
    get verbProgressive() { return jt("mission_orders_single_pdf.verb_progressive"); },
    get verbPast() { return jt("mission_orders_single_pdf.verb_past"); },
    category: "mission-orders",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    get phaseLabels() {
      return {
        validation: jt("mission_orders_single_pdf.phase_validation"),
        generate: jt("mission_orders_single_pdf.phase_generate"),
        upload_artifact: jt("mission_orders_single_pdf.phase_upload_artifact"),
      };
    },
    phaseWeights: [
      { name: "validation", weight: 10 },
      { name: "generate", weight: 70 },
      { name: "upload_artifact", weight: 20 },
    ],
    successToast: () => jt("mission_orders_single_pdf.success"),
    failureToast: (job) =>
      jt("mission_orders_single_pdf.failure", {
        error: job.error || jt("error_fallback_unknown"),
      }),
  },
  [JobKinds.MissionOrdersSingleEmail]: {
    kind: JobKinds.MissionOrdersSingleEmail,
    get shortLabel() { return jt("mission_orders_single_email.short"); },
    get fullLabel() { return jt("mission_orders_single_email.full"); },
    get verbPresent() { return jt("mission_orders_single_email.verb_present"); },
    get verbProgressive() { return jt("mission_orders_single_email.verb_progressive"); },
    get verbPast() { return jt("mission_orders_single_email.verb_past"); },
    category: "mission-orders",
    isBulk: false,
    icon: <CheckCircleIcon className="h-4 w-4" />,
    successToast: () => jt("mission_orders_single_email.success"),
    failureToast: (job) =>
      jt("mission_orders_single_email.failure", {
        error: job.error || jt("error_fallback_unknown"),
      }),
  },
  [JobKinds.MatchSheetsBulkEmail]: {
    kind: JobKinds.MatchSheetsBulkEmail,
    get shortLabel() { return jt("match_sheets_bulk_email.short"); },
    get fullLabel() { return jt("match_sheets_bulk_email.full"); },
    get verbPresent() { return jt("match_sheets_bulk_email.verb_present"); },
    get verbProgressive() { return jt("match_sheets_bulk_email.verb_progressive"); },
    get verbPast() { return jt("match_sheets_bulk_email.verb_past"); },
    category: "emails",
    isBulk: true,
    icon: <CheckCircleIcon className="h-4 w-4" />,
    get phaseLabels() {
      return {
        validation: jt("match_sheets_bulk_email.phase_validation"),
        prepare_recipients: jt("match_sheets_bulk_email.phase_prepare_recipients"),
        render_templates: jt("match_sheets_bulk_email.phase_render_templates"),
        send_emails: jt("match_sheets_bulk_email.phase_send_emails"),
        cleanup: jt("match_sheets_bulk_email.phase_cleanup"),
      };
    },
    phaseWeights: [
      { name: "validation", weight: 5 },
      { name: "prepare_recipients", weight: 10 },
      { name: "render_templates", weight: 25 },
      { name: "send_emails", weight: 50 },
      { name: "cleanup", weight: 10 },
    ],
    successToast: (job) => {
      const stats = job.meta?.email_stats || job.payload?.email_stats;
      if (stats)
        return jt("match_sheets_bulk_email.success", {
          completed: stats.completed,
          total: stats.total,
        });
      return jt("match_sheets_bulk_email.success_nodata");
    },
    failureToast: (job) =>
      jt("match_sheets_bulk_email.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.MessagingBulkEmail]: {
    kind: JobKinds.MessagingBulkEmail,
    get shortLabel() { return jt("messaging_bulk_email.short"); },
    get fullLabel() { return jt("messaging_bulk_email.full"); },
    get verbPresent() { return jt("messaging_bulk_email.verb_present"); },
    get verbProgressive() { return jt("messaging_bulk_email.verb_progressive"); },
    get verbPast() { return jt("messaging_bulk_email.verb_past"); },
    category: "messaging",
    isBulk: true,
    icon: <CheckCircleIcon className="h-4 w-4" />,
    successToast: () => jt("messaging_bulk_email.success"),
    failureToast: (job) =>
      jt("messaging_bulk_email.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.DistanceRefresh]: {
    kind: JobKinds.DistanceRefresh,
    get shortLabel() { return jt("distance_refresh.short"); },
    get fullLabel() { return jt("distance_refresh.full"); },
    get verbPresent() { return jt("distance_refresh.verb_present"); },
    get verbProgressive() { return jt("distance_refresh.verb_progressive"); },
    get verbPast() { return jt("distance_refresh.verb_past"); },
    category: "maintenance",
    isBulk: true,
    icon: <AlertTriangleIcon className="h-4 w-4" />,
    successToast: (job) => {
      const updated =
        job.meta?.updated ?? job.processed ?? job.payload?.updated;
      if (updated == null) return ""; // skip ambiguous toast when no data yet
      return updated > 0
        ? jt("distance_refresh.success_many", { count: updated })
        : jt("distance_refresh.success_zero");
    },
    failureToast: (job) =>
      jt("distance_refresh.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.DisciplinaryPvPdf]: {
    kind: JobKinds.DisciplinaryPvPdf,
    get shortLabel() { return jt("disciplinary_pv_pdf.short"); },
    get fullLabel() { return jt("disciplinary_pv_pdf.full"); },
    get verbPresent() { return jt("disciplinary_pv_pdf.verb_present"); },
    get verbProgressive() { return jt("disciplinary_pv_pdf.verb_progressive"); },
    get verbPast() { return jt("disciplinary_pv_pdf.verb_past"); },
    category: "disciplinary",
    isBulk: false,
    icon: <GavelIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      // The worker served the file of the last generation: nothing it prints changed.
      if (job.result?.cached === true) return jt("disciplinary_pv_pdf.success_cached");
      const n = job.result?.reportNumber;
      return n
        ? jt("disciplinary_pv_pdf.success", { number: n })
        : jt("disciplinary_pv_pdf.success_nonumber");
    },
    failureToast: (job) =>
      jt("disciplinary_pv_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.SanctionsPdf]: {
    kind: JobKinds.SanctionsPdf,
    get shortLabel() { return jt("sanctions_pdf.short"); },
    get fullLabel() { return jt("sanctions_pdf.full"); },
    get verbPresent() { return jt("sanctions_pdf.verb_present"); },
    get verbProgressive() { return jt("sanctions_pdf.verb_progressive"); },
    get verbPast() { return jt("sanctions_pdf.verb_past"); },
    category: "disciplinary",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    get phaseLabels() {
      return {
        fetch_data: jt("sanctions_pdf.phase_fetch_data"),
        render_pdf: jt("sanctions_pdf.phase_render_pdf"),
        upload_storage: jt("sanctions_pdf.phase_upload_storage"),
      };
    },
    phaseWeights: [
        { name: "fetch_data", weight: 30 },
        { name: "render_pdf", weight: 50 },
        { name: "upload_storage", weight: 20 },
    ],
    successToast: (job) => {
        const file = job.payload?.fileName;
        return file
          ? jt("sanctions_pdf.success", { file })
          : jt("sanctions_pdf.success_nofile");
    },
    failureToast: () => jt("sanctions_pdf.failure"),
  },
  [JobKinds.StatementsSinglePdf]: {
    kind: JobKinds.StatementsSinglePdf,
    get shortLabel() { return jt("statements_single_pdf.short"); },
    get fullLabel() { return jt("statements_single_pdf.full"); },
    get verbPresent() { return jt("statements_single_pdf.verb_present"); },
    get verbProgressive() { return jt("statements_single_pdf.verb_progressive"); },
    get verbPast() { return jt("statements_single_pdf.verb_past"); },
    category: "statements",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    get phaseLabels() {
      return {
        validation: jt("statements_single_pdf.phase_validation"),
        fetch_data: jt("statements_single_pdf.phase_fetch_data"),
        generate_pdf: jt("statements_single_pdf.phase_generate_pdf"),
        upload_artifact: jt("statements_single_pdf.phase_upload_artifact"),
      };
    },
    phaseWeights: [
      { name: "validation", weight: 10 },
      { name: "fetch_data", weight: 20 },
      { name: "generate_pdf", weight: 50 },
      { name: "upload_artifact", weight: 20 },
    ],
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("statements_single_pdf.success", { file })
        : jt("statements_single_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("statements_single_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.StatementsBulkPdf]: {
    kind: JobKinds.StatementsBulkPdf,
    get shortLabel() { return jt("statements_bulk_pdf.short"); },
    get fullLabel() { return jt("statements_bulk_pdf.full"); },
    get verbPresent() { return jt("statements_bulk_pdf.verb_present"); },
    get verbProgressive() { return jt("statements_bulk_pdf.verb_progressive"); },
    get verbPast() { return jt("statements_bulk_pdf.verb_past"); },
    category: "statements",
    isBulk: true,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      const total = job.result?.totalStatements || job.payload?.totalStatements;
      return file
        ? jt("statements_bulk_pdf.success", { file, total: total || 0 })
        : jt("statements_bulk_pdf.success_nofile", { total: total || 0 });
    },
    failureToast: (job) =>
      jt("statements_bulk_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.StatementsBulkPdfChunk]: {
    kind: JobKinds.StatementsBulkPdfChunk,
    get shortLabel() { return jt("statements_bulk_pdf_chunk.short"); },
    get fullLabel() { return jt("statements_bulk_pdf_chunk.full"); },
    get verbPresent() { return jt("statements_bulk_pdf_chunk.verb_present"); },
    get verbProgressive() { return jt("statements_bulk_pdf_chunk.verb_progressive"); },
    get verbPast() { return jt("statements_bulk_pdf_chunk.verb_past"); },
    category: "statements",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: false,
  },
  [JobKinds.StatementsBulkPdfMerge]: {
    kind: JobKinds.StatementsBulkPdfMerge,
    get shortLabel() { return jt("statements_bulk_pdf_merge.short"); },
    get fullLabel() { return jt("statements_bulk_pdf_merge.full"); },
    get verbPresent() { return jt("statements_bulk_pdf_merge.verb_present"); },
    get verbProgressive() { return jt("statements_bulk_pdf_merge.verb_progressive"); },
    get verbPast() { return jt("statements_bulk_pdf_merge.verb_past"); },
    category: "statements",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
  },
  [JobKinds.CategoryRecapPdf]: {
    kind: JobKinds.CategoryRecapPdf,
    get shortLabel() { return jt("category_recap_pdf.short"); },
    get fullLabel() { return jt("category_recap_pdf.full"); },
    get verbPresent() { return jt("category_recap_pdf.verb_present"); },
    get verbProgressive() { return jt("category_recap_pdf.verb_progressive"); },
    get verbPast() { return jt("category_recap_pdf.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("category_recap_pdf.success", { file })
        : jt("category_recap_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("category_recap_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.CalendarPdf]: {
    kind: JobKinds.CalendarPdf,
    get shortLabel() { return jt("calendar_pdf.short"); },
    get fullLabel() { return jt("calendar_pdf.full"); },
    get verbPresent() { return jt("calendar_pdf.verb_present"); },
    get verbProgressive() { return jt("calendar_pdf.verb_progressive"); },
    get verbPast() { return jt("calendar_pdf.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("calendar_pdf.success", { file })
        : jt("calendar_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("calendar_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.CalendarOptimize]: {
    kind: JobKinds.CalendarOptimize,
    get shortLabel() { return jt("calendar_optimize.short"); },
    get fullLabel() { return jt("calendar_optimize.full"); },
    get verbPresent() { return jt("calendar_optimize.verb_present"); },
    get verbProgressive() { return jt("calendar_optimize.verb_progressive"); },
    get verbPast() { return jt("calendar_optimize.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: false,
    successToast: () => jt("calendar_optimize.success"),
    failureToast: (job) =>
      jt("calendar_optimize.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.CalendarDrawGridPdf]: {
    kind: JobKinds.CalendarDrawGridPdf,
    get shortLabel() { return jt("calendar_draw_grid_pdf.short"); },
    get fullLabel() { return jt("calendar_draw_grid_pdf.full"); },
    get verbPresent() { return jt("calendar_draw_grid_pdf.verb_present"); },
    get verbProgressive() { return jt("calendar_draw_grid_pdf.verb_progressive"); },
    get verbPast() { return jt("calendar_draw_grid_pdf.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("calendar_draw_grid_pdf.success", { file })
        : jt("calendar_draw_grid_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("calendar_draw_grid_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.CalendarDrawBallotsPdf]: {
    kind: JobKinds.CalendarDrawBallotsPdf,
    get shortLabel() { return jt("calendar_draw_ballots_pdf.short"); },
    get fullLabel() { return jt("calendar_draw_ballots_pdf.full"); },
    get verbPresent() { return jt("calendar_draw_ballots_pdf.verb_present"); },
    get verbProgressive() { return jt("calendar_draw_ballots_pdf.verb_progressive"); },
    get verbPast() { return jt("calendar_draw_ballots_pdf.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("calendar_draw_ballots_pdf.success", { file })
        : jt("calendar_draw_ballots_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("calendar_draw_ballots_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.CalendarDrawClubBallotsPdf]: {
    kind: JobKinds.CalendarDrawClubBallotsPdf,
    get shortLabel() { return jt("calendar_draw_club_ballots_pdf.short"); },
    get fullLabel() { return jt("calendar_draw_club_ballots_pdf.full"); },
    get verbPresent() { return jt("calendar_draw_club_ballots_pdf.verb_present"); },
    get verbProgressive() { return jt("calendar_draw_club_ballots_pdf.verb_progressive"); },
    get verbPast() { return jt("calendar_draw_club_ballots_pdf.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("calendar_draw_club_ballots_pdf.success", { file })
        : jt("calendar_draw_club_ballots_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("calendar_draw_club_ballots_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.CalendarDrawPvPdf]: {
    kind: JobKinds.CalendarDrawPvPdf,
    get shortLabel() { return jt("calendar_draw_pv_pdf.short"); },
    get fullLabel() { return jt("calendar_draw_pv_pdf.full"); },
    get verbPresent() { return jt("calendar_draw_pv_pdf.verb_present"); },
    get verbProgressive() { return jt("calendar_draw_pv_pdf.verb_progressive"); },
    get verbPast() { return jt("calendar_draw_pv_pdf.verb_past"); },
    category: "exports",
    isBulk: false,
    icon: <PrinterIcon className="h-4 w-4" />,
    hasArtifact: true,
    successToast: (job) => {
      const file = job.result?.fileName || job.payload?.fileName;
      return file
        ? jt("calendar_draw_pv_pdf.success", { file })
        : jt("calendar_draw_pv_pdf.success_nofile");
    },
    failureToast: (job) =>
      jt("calendar_draw_pv_pdf.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.FafAuditFixGoals]: {
    kind: JobKinds.FafAuditFixGoals,
    get shortLabel() { return jt("faf_audit_fix_goals.short"); },
    get fullLabel() { return jt("faf_audit_fix_goals.full"); },
    get verbPresent() { return jt("faf_audit_fix_goals.verb_present"); },
    get verbProgressive() { return jt("faf_audit_fix_goals.verb_progressive"); },
    get verbPast() { return jt("faf_audit_fix_goals.verb_past"); },
    category: "audit",
    isBulk: true,
    icon: <CheckCircleIcon className="h-4 w-4" />,
    get phaseLabels() {
      return {
        audit: jt("faf_audit_fix_goals.phase_audit"),
        fix: jt("faf_audit_fix_goals.phase_fix"),
        refresh: jt("faf_audit_fix_goals.phase_refresh"),
      };
    },
    phaseWeights: [
      { name: "audit", weight: 20 },
      { name: "fix", weight: 70 },
      { name: "refresh", weight: 10 },
    ],
    successToast: (job) => {
      const fixed = job.result?.fixedMatches ?? job.processed ?? 0;
      return fixed > 0
        ? jt("faf_audit_fix_goals.success_many", { count: fixed })
        : jt("faf_audit_fix_goals.success_zero");
    },
    failureToast: (job) =>
      jt("faf_audit_fix_goals.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.FafExtractAi]: {
    kind: JobKinds.FafExtractAi,
    get shortLabel() { return jt("faf_extract_ai.short"); },
    get fullLabel() { return jt("faf_extract_ai.full"); },
    get verbPresent() { return jt("faf_extract_ai.verb_present"); },
    get verbProgressive() { return jt("faf_extract_ai.verb_progressive"); },
    get verbPast() { return jt("faf_extract_ai.verb_past"); },
    category: "disciplinary",
    isBulk: false,
    icon: <Sparkles className="h-4 w-4" />,
    get phaseLabels() {
      return {
        fetch_report: jt("faf_extract_ai.phase_fetch_report"),
        fetch_roster: jt("faf_extract_ai.phase_fetch_roster"),
        call_ai: jt("faf_extract_ai.phase_call_ai"),
        persist: jt("faf_extract_ai.phase_persist"),
      };
    },
    phaseWeights: [
      { name: "fetch_report", weight: 10 },
      { name: "fetch_roster", weight: 15 },
      { name: "call_ai", weight: 65 },
      { name: "persist", weight: 10 },
    ],
    successToast: (job) => {
      const count = job.result?.eventCount ?? 0;
      if (job.result?.cached) {
        if (count <= 0) return jt("faf_extract_ai.success_cached_zero");
        return jt(
          count > 1 ? "faf_extract_ai.success_cached_many" : "faf_extract_ai.success_cached_one",
          { count }
        );
      }
      if (count <= 0) return jt("faf_extract_ai.success_zero");
      return jt(
        count > 1 ? "faf_extract_ai.success_many" : "faf_extract_ai.success_one",
        { count }
      );
    },
    failureToast: (job) =>
      jt("faf_extract_ai.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.FafSyncMatch]: {
    kind: JobKinds.FafSyncMatch,
    get shortLabel() { return jt("faf_sync_match.short"); },
    get fullLabel() { return jt("faf_sync_match.full"); },
    get verbPresent() { return jt("faf_sync_match.verb_present"); },
    get verbProgressive() { return jt("faf_sync_match.verb_progressive"); },
    get verbPast() { return jt("faf_sync_match.verb_past"); },
    category: "disciplinary",
    isBulk: false,
    icon: <RefreshCw className="h-4 w-4" />,
    get phaseLabels() {
      return {
        "direct-sync": jt("faf_sync_match.phase_direct_sync"),
        "github-workflow": jt("faf_sync_match.phase_github_workflow"),
        save_events: jt("faf_sync_match.phase_save_events"),
        save_pdfs: jt("faf_sync_match.phase_save_pdfs"),
      };
    },
    phaseWeights: [
      { name: "direct-sync", weight: 60 },
      { name: "save_events", weight: 25 },
      { name: "save_pdfs", weight: 15 },
    ],
    successToast: (job) => {
      const n = job.result?.eventCount ?? 0;
      const viaGithub = job.result?.via === "github";
      if (n <= 0) {
        return jt(viaGithub ? "faf_sync_match.success_github_zero" : "faf_sync_match.success_zero");
      }
      const many = n > 1;
      const key = viaGithub
        ? many ? "faf_sync_match.success_github_many" : "faf_sync_match.success_github_one"
        : many ? "faf_sync_match.success_many" : "faf_sync_match.success_one";
      return jt(key, { count: n });
    },
    failureToast: (job) =>
      jt("faf_sync_match.failure", { error: job.error || jt("error_fallback") }),
  },
  [JobKinds.AccountAuditCreate]: {
    kind: JobKinds.AccountAuditCreate,
    get shortLabel() { return jt("account_audit_create.short"); },
    get fullLabel() { return jt("account_audit_create.full"); },
    get verbPresent() { return jt("account_audit_create.verb_present"); },
    get verbProgressive() { return jt("account_audit_create.verb_progressive"); },
    get verbPast() { return jt("account_audit_create.verb_past"); },
    category: "audit",
    isBulk: true,
    icon: <CheckCircleIcon className="h-4 w-4" />,
    get phaseLabels() {
      return {
        fetch: jt("account_audit_create.phase_fetch"),
        process: jt("account_audit_create.phase_process"),
        email: jt("account_audit_create.phase_email"),
      };
    },
    phaseWeights: [
      { name: "fetch", weight: 10 },
      { name: "process", weight: 70 },
      { name: "email", weight: 20 },
    ],
    successToast: (job) => {
      const created = job.result?.accounts_created ?? 0;
      const linked = job.result?.accounts_linked ?? 0;
      return jt("account_audit_create.success", { created, linked });
    },
    failureToast: (job) =>
      jt("account_audit_create.failure", { error: job.error || jt("error_fallback") }),
  },
};

// Localized status labels for reuse. Getters re-evaluate on each access so a
// language switch is reflected without recomputing the map.
export const STATUS_DISPLAY: Record<string, string> = {
  get pending() { return i18n.t("management:job_kinds.status.pending"); },
  get processing() { return i18n.t("management:job_kinds.status.processing"); },
  get retrying() { return i18n.t("management:job_kinds.status.retrying"); },
  get completed() { return i18n.t("management:job_kinds.status.completed"); },
  get failed() { return i18n.t("management:job_kinds.status.failed"); },
  get cancelled() { return i18n.t("management:job_kinds.status.cancelled"); },
  get paused() { return i18n.t("management:job_kinds.status.paused"); },
};

/** Return a localized status display map using i18n keys from the management namespace. */
export function createStatusDisplay(
  t: (key: string) => string
): Record<string, string> {
  return {
    pending: t("job_kinds.status.pending"),
    processing: t("job_kinds.status.processing"),
    retrying: t("job_kinds.status.retrying"),
    completed: t("job_kinds.status.completed"),
    failed: t("job_kinds.status.failed"),
    cancelled: t("job_kinds.status.cancelled"),
    paused: t("job_kinds.status.paused"),
  };
}

/**
 * Return a fully-localized JOB_KIND_META map.
 * Pass the `t` function from `useTranslation("management")`.
 * Non-string fields (category, isBulk, icon, hasArtifact, phaseWeights,
 * successToast, failureToast) are taken directly from the static JOB_KIND_META.
 */
export function createJobKindMeta(
  t: (key: string, opts?: Record<string, unknown>) => string
): Record<JobKind, JobKindMeta> {
  const base = JOB_KIND_META;
  return {
    [JobKinds.MissionOrdersBulkPdf]: {
      ...base[JobKinds.MissionOrdersBulkPdf],
      shortLabel: t("job_kinds.mission_orders_bulk_pdf.short"),
      fullLabel: t("job_kinds.mission_orders_bulk_pdf.full"),
      verbPresent: t("job_kinds.mission_orders_bulk_pdf.verb_present"),
      verbProgressive: t("job_kinds.mission_orders_bulk_pdf.verb_progressive"),
      verbPast: t("job_kinds.mission_orders_bulk_pdf.verb_past"),
    },
    [JobKinds.MissionOrdersBulkPdfChunk]: {
      ...base[JobKinds.MissionOrdersBulkPdfChunk],
      shortLabel: t("job_kinds.mission_orders_bulk_pdf_chunk.short"),
      fullLabel: t("job_kinds.mission_orders_bulk_pdf_chunk.full"),
      verbPresent: t("job_kinds.mission_orders_bulk_pdf_chunk.verb_present"),
      verbProgressive: t("job_kinds.mission_orders_bulk_pdf_chunk.verb_progressive"),
      verbPast: t("job_kinds.mission_orders_bulk_pdf_chunk.verb_past"),
    },
    [JobKinds.MissionOrdersBulkPdfMerge]: {
      ...base[JobKinds.MissionOrdersBulkPdfMerge],
      shortLabel: t("job_kinds.mission_orders_bulk_pdf_merge.short"),
      fullLabel: t("job_kinds.mission_orders_bulk_pdf_merge.full"),
      verbPresent: t("job_kinds.mission_orders_bulk_pdf_merge.verb_present"),
      verbProgressive: t("job_kinds.mission_orders_bulk_pdf_merge.verb_progressive"),
      verbPast: t("job_kinds.mission_orders_bulk_pdf_merge.verb_past"),
    },
    [JobKinds.MissionOrdersSinglePdf]: {
      ...base[JobKinds.MissionOrdersSinglePdf],
      shortLabel: t("job_kinds.mission_orders_single_pdf.short"),
      fullLabel: t("job_kinds.mission_orders_single_pdf.full"),
      verbPresent: t("job_kinds.mission_orders_single_pdf.verb_present"),
      verbProgressive: t("job_kinds.mission_orders_single_pdf.verb_progressive"),
      verbPast: t("job_kinds.mission_orders_single_pdf.verb_past"),
      phaseLabels: {
        validation: t("job_kinds.mission_orders_single_pdf.phase_validation"),
        generate: t("job_kinds.mission_orders_single_pdf.phase_generate"),
        upload_artifact: t("job_kinds.mission_orders_single_pdf.phase_upload_artifact"),
      },
    },
    [JobKinds.MissionOrdersSingleEmail]: {
      ...base[JobKinds.MissionOrdersSingleEmail],
      shortLabel: t("job_kinds.mission_orders_single_email.short"),
      fullLabel: t("job_kinds.mission_orders_single_email.full"),
      verbPresent: t("job_kinds.mission_orders_single_email.verb_present"),
      verbProgressive: t("job_kinds.mission_orders_single_email.verb_progressive"),
      verbPast: t("job_kinds.mission_orders_single_email.verb_past"),
    },
    [JobKinds.MatchSheetsBulkEmail]: {
      ...base[JobKinds.MatchSheetsBulkEmail],
      shortLabel: t("job_kinds.match_sheets_bulk_email.short"),
      fullLabel: t("job_kinds.match_sheets_bulk_email.full"),
      verbPresent: t("job_kinds.match_sheets_bulk_email.verb_present"),
      verbProgressive: t("job_kinds.match_sheets_bulk_email.verb_progressive"),
      verbPast: t("job_kinds.match_sheets_bulk_email.verb_past"),
      phaseLabels: {
        validation: t("job_kinds.match_sheets_bulk_email.phase_validation"),
        prepare_recipients: t("job_kinds.match_sheets_bulk_email.phase_prepare_recipients"),
        render_templates: t("job_kinds.match_sheets_bulk_email.phase_render_templates"),
        send_emails: t("job_kinds.match_sheets_bulk_email.phase_send_emails"),
        cleanup: t("job_kinds.match_sheets_bulk_email.phase_cleanup"),
      },
    },
    [JobKinds.MessagingBulkEmail]: {
      ...base[JobKinds.MessagingBulkEmail],
      shortLabel: t("job_kinds.messaging_bulk_email.short"),
      fullLabel: t("job_kinds.messaging_bulk_email.full"),
      verbPresent: t("job_kinds.messaging_bulk_email.verb_present"),
      verbProgressive: t("job_kinds.messaging_bulk_email.verb_progressive"),
      verbPast: t("job_kinds.messaging_bulk_email.verb_past"),
    },
    [JobKinds.DistanceRefresh]: {
      ...base[JobKinds.DistanceRefresh],
      shortLabel: t("job_kinds.distance_refresh.short"),
      fullLabel: t("job_kinds.distance_refresh.full"),
      verbPresent: t("job_kinds.distance_refresh.verb_present"),
      verbProgressive: t("job_kinds.distance_refresh.verb_progressive"),
      verbPast: t("job_kinds.distance_refresh.verb_past"),
    },
    [JobKinds.DisciplinaryPvPdf]: {
      ...base[JobKinds.DisciplinaryPvPdf],
      shortLabel: t("job_kinds.disciplinary_pv_pdf.short"),
      fullLabel: t("job_kinds.disciplinary_pv_pdf.full"),
      verbPresent: t("job_kinds.disciplinary_pv_pdf.verb_present"),
      verbProgressive: t("job_kinds.disciplinary_pv_pdf.verb_progressive"),
      verbPast: t("job_kinds.disciplinary_pv_pdf.verb_past"),
    },
    [JobKinds.SanctionsPdf]: {
      ...base[JobKinds.SanctionsPdf],
      shortLabel: t("job_kinds.sanctions_pdf.short"),
      fullLabel: t("job_kinds.sanctions_pdf.full"),
      verbPresent: t("job_kinds.sanctions_pdf.verb_present"),
      verbProgressive: t("job_kinds.sanctions_pdf.verb_progressive"),
      verbPast: t("job_kinds.sanctions_pdf.verb_past"),
      phaseLabels: {
        fetch_data: t("job_kinds.sanctions_pdf.phase_fetch_data"),
        render_pdf: t("job_kinds.sanctions_pdf.phase_render_pdf"),
        upload_storage: t("job_kinds.sanctions_pdf.phase_upload_storage"),
      },
    },
    [JobKinds.StatementsSinglePdf]: {
      ...base[JobKinds.StatementsSinglePdf],
      shortLabel: t("job_kinds.statements_single_pdf.short"),
      fullLabel: t("job_kinds.statements_single_pdf.full"),
      verbPresent: t("job_kinds.statements_single_pdf.verb_present"),
      verbProgressive: t("job_kinds.statements_single_pdf.verb_progressive"),
      verbPast: t("job_kinds.statements_single_pdf.verb_past"),
      phaseLabels: {
        validation: t("job_kinds.statements_single_pdf.phase_validation"),
        fetch_data: t("job_kinds.statements_single_pdf.phase_fetch_data"),
        generate_pdf: t("job_kinds.statements_single_pdf.phase_generate_pdf"),
        upload_artifact: t("job_kinds.statements_single_pdf.phase_upload_artifact"),
      },
    },
    [JobKinds.StatementsBulkPdf]: {
      ...base[JobKinds.StatementsBulkPdf],
      shortLabel: t("job_kinds.statements_bulk_pdf.short"),
      fullLabel: t("job_kinds.statements_bulk_pdf.full"),
      verbPresent: t("job_kinds.statements_bulk_pdf.verb_present"),
      verbProgressive: t("job_kinds.statements_bulk_pdf.verb_progressive"),
      verbPast: t("job_kinds.statements_bulk_pdf.verb_past"),
    },
    [JobKinds.StatementsBulkPdfChunk]: {
      ...base[JobKinds.StatementsBulkPdfChunk],
      shortLabel: t("job_kinds.statements_bulk_pdf_chunk.short"),
      fullLabel: t("job_kinds.statements_bulk_pdf_chunk.full"),
      verbPresent: t("job_kinds.statements_bulk_pdf_chunk.verb_present"),
      verbProgressive: t("job_kinds.statements_bulk_pdf_chunk.verb_progressive"),
      verbPast: t("job_kinds.statements_bulk_pdf_chunk.verb_past"),
    },
    [JobKinds.StatementsBulkPdfMerge]: {
      ...base[JobKinds.StatementsBulkPdfMerge],
      shortLabel: t("job_kinds.statements_bulk_pdf_merge.short"),
      fullLabel: t("job_kinds.statements_bulk_pdf_merge.full"),
      verbPresent: t("job_kinds.statements_bulk_pdf_merge.verb_present"),
      verbProgressive: t("job_kinds.statements_bulk_pdf_merge.verb_progressive"),
      verbPast: t("job_kinds.statements_bulk_pdf_merge.verb_past"),
    },
    [JobKinds.CategoryRecapPdf]: {
      ...base[JobKinds.CategoryRecapPdf],
      shortLabel: t("job_kinds.category_recap_pdf.short"),
      fullLabel: t("job_kinds.category_recap_pdf.full"),
      verbPresent: t("job_kinds.category_recap_pdf.verb_present"),
      verbProgressive: t("job_kinds.category_recap_pdf.verb_progressive"),
      verbPast: t("job_kinds.category_recap_pdf.verb_past"),
    },
    [JobKinds.CalendarPdf]: {
      ...base[JobKinds.CalendarPdf],
      shortLabel: t("job_kinds.calendar_pdf.short"),
      fullLabel: t("job_kinds.calendar_pdf.full"),
      verbPresent: t("job_kinds.calendar_pdf.verb_present"),
      verbProgressive: t("job_kinds.calendar_pdf.verb_progressive"),
      verbPast: t("job_kinds.calendar_pdf.verb_past"),
    },
    [JobKinds.CalendarOptimize]: {
      ...base[JobKinds.CalendarOptimize],
      shortLabel: t("job_kinds.calendar_optimize.short"),
      fullLabel: t("job_kinds.calendar_optimize.full"),
      verbPresent: t("job_kinds.calendar_optimize.verb_present"),
      verbProgressive: t("job_kinds.calendar_optimize.verb_progressive"),
      verbPast: t("job_kinds.calendar_optimize.verb_past"),
    },
    [JobKinds.CalendarDrawGridPdf]: {
      ...base[JobKinds.CalendarDrawGridPdf],
      shortLabel: t("job_kinds.calendar_draw_grid_pdf.short"),
      fullLabel: t("job_kinds.calendar_draw_grid_pdf.full"),
      verbPresent: t("job_kinds.calendar_draw_grid_pdf.verb_present"),
      verbProgressive: t("job_kinds.calendar_draw_grid_pdf.verb_progressive"),
      verbPast: t("job_kinds.calendar_draw_grid_pdf.verb_past"),
    },
    [JobKinds.CalendarDrawBallotsPdf]: {
      ...base[JobKinds.CalendarDrawBallotsPdf],
      shortLabel: t("job_kinds.calendar_draw_ballots_pdf.short"),
      fullLabel: t("job_kinds.calendar_draw_ballots_pdf.full"),
      verbPresent: t("job_kinds.calendar_draw_ballots_pdf.verb_present"),
      verbProgressive: t("job_kinds.calendar_draw_ballots_pdf.verb_progressive"),
      verbPast: t("job_kinds.calendar_draw_ballots_pdf.verb_past"),
    },
    [JobKinds.CalendarDrawClubBallotsPdf]: {
      ...base[JobKinds.CalendarDrawClubBallotsPdf],
      shortLabel: t("job_kinds.calendar_draw_club_ballots_pdf.short"),
      fullLabel: t("job_kinds.calendar_draw_club_ballots_pdf.full"),
      verbPresent: t("job_kinds.calendar_draw_club_ballots_pdf.verb_present"),
      verbProgressive: t("job_kinds.calendar_draw_club_ballots_pdf.verb_progressive"),
      verbPast: t("job_kinds.calendar_draw_club_ballots_pdf.verb_past"),
    },
    [JobKinds.CalendarDrawPvPdf]: {
      ...base[JobKinds.CalendarDrawPvPdf],
      shortLabel: t("job_kinds.calendar_draw_pv_pdf.short"),
      fullLabel: t("job_kinds.calendar_draw_pv_pdf.full"),
      verbPresent: t("job_kinds.calendar_draw_pv_pdf.verb_present"),
      verbProgressive: t("job_kinds.calendar_draw_pv_pdf.verb_progressive"),
      verbPast: t("job_kinds.calendar_draw_pv_pdf.verb_past"),
    },
    [JobKinds.FafAuditFixGoals]: {
      ...base[JobKinds.FafAuditFixGoals],
      shortLabel: t("job_kinds.faf_audit_fix_goals.short"),
      fullLabel: t("job_kinds.faf_audit_fix_goals.full"),
      verbPresent: t("job_kinds.faf_audit_fix_goals.verb_present"),
      verbProgressive: t("job_kinds.faf_audit_fix_goals.verb_progressive"),
      verbPast: t("job_kinds.faf_audit_fix_goals.verb_past"),
      phaseLabels: {
        audit: t("job_kinds.faf_audit_fix_goals.phase_audit"),
        fix: t("job_kinds.faf_audit_fix_goals.phase_fix"),
        refresh: t("job_kinds.faf_audit_fix_goals.phase_refresh"),
      },
    },
    [JobKinds.FafExtractAi]: {
      ...base[JobKinds.FafExtractAi],
      shortLabel: t("job_kinds.faf_extract_ai.short"),
      fullLabel: t("job_kinds.faf_extract_ai.full"),
      verbPresent: t("job_kinds.faf_extract_ai.verb_present"),
      verbProgressive: t("job_kinds.faf_extract_ai.verb_progressive"),
      verbPast: t("job_kinds.faf_extract_ai.verb_past"),
      phaseLabels: {
        fetch_report: t("job_kinds.faf_extract_ai.phase_fetch_report"),
        fetch_roster: t("job_kinds.faf_extract_ai.phase_fetch_roster"),
        call_ai: t("job_kinds.faf_extract_ai.phase_call_ai"),
        persist: t("job_kinds.faf_extract_ai.phase_persist"),
      },
    },
    [JobKinds.FafSyncMatch]: {
      ...base[JobKinds.FafSyncMatch],
      shortLabel: t("job_kinds.faf_sync_match.short"),
      fullLabel: t("job_kinds.faf_sync_match.full"),
      verbPresent: t("job_kinds.faf_sync_match.verb_present"),
      verbProgressive: t("job_kinds.faf_sync_match.verb_progressive"),
      verbPast: t("job_kinds.faf_sync_match.verb_past"),
      phaseLabels: {
        "direct-sync": t("job_kinds.faf_sync_match.phase_direct_sync"),
        "github-workflow": t("job_kinds.faf_sync_match.phase_github_workflow"),
        save_events: t("job_kinds.faf_sync_match.phase_save_events"),
        save_pdfs: t("job_kinds.faf_sync_match.phase_save_pdfs"),
      },
    },
    [JobKinds.AccountAuditCreate]: {
      ...base[JobKinds.AccountAuditCreate],
      shortLabel: t("job_kinds.account_audit_create.short"),
      fullLabel: t("job_kinds.account_audit_create.full"),
      verbPresent: t("job_kinds.account_audit_create.verb_present"),
      verbProgressive: t("job_kinds.account_audit_create.verb_progressive"),
      verbPast: t("job_kinds.account_audit_create.verb_past"),
      phaseLabels: {
        fetch: t("job_kinds.account_audit_create.phase_fetch"),
        process: t("job_kinds.account_audit_create.phase_process"),
        email: t("job_kinds.account_audit_create.phase_email"),
      },
    },
  };
}
